import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { BlockComponent } from "@components/container-block/block.component";
import { InfoComponent } from "@components/info/info.component";
import { InputComponent } from "@components/input/input.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { groupBy, orderBy, remove } from "lodash";
import { Subject, from } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { SimpleSelectionItem } from "~app/common/components/selector/simple-selection-item";
import { ToastService } from "~app/common/components/toast/toast.service";
import { SelectionEventType } from "~app/common/components/tree-selector/SelectionEventType";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { ProduktionsmallHandelssortimentDTO } from "~app/common/models/produktionsmall/v2/ProduktionsmallHandelssortimentDTO";
import { ProduktionsmallQueryResponseDTO } from "~app/common/models/produktionsmall/v2/ProduktionsmallQueryResponseDTO";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager, ICommandManager } from "~app/common/services/cqs/commandManager";
import { CommonTexts } from "~app/common/texts/common.texts";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { AddProduktionsmallCommand } from "~app/features/planera/commands/AddProduktionsmallCommand";
import { AddProduktionsmallHandelssortimentCommand } from "~app/features/planera/commands/AddProduktionsmallHandelssortimentCommand";
import { AddProduktionsmallProduktCommand } from "~app/features/planera/commands/AddProduktionsmallProduktCommand";
import { AddProduktionsmallProduktinstruktionCommand } from "~app/features/planera/commands/AddProduktionsmallProduktinstruktionCommand";
import { DeleteProduktionsmallCascadeCommand } from "~app/features/planera/commands/DeleteProduktionsmallCascadeCommand";
import { DeleteProduktionsmallHandelssortimentCommand } from "~app/features/planera/commands/DeleteProduktionsmallHandelssortimentCommand";
import { DeleteProduktionsmallProduktCommand } from "~app/features/planera/commands/DeleteProduktionsmallProduktCommand";
import { DeleteProduktionsmallProduktinstruktionCommand } from "~app/features/planera/commands/DeleteProduktionsmallProduktinstruktionCommand";
import { ProduktionsmallHandelssortimentBaseCommand } from "~app/features/planera/commands/ProduktionsmallHandelssortimentBaseCommand";
import { ProduktionsmallProduktBaseCommand } from "~app/features/planera/commands/ProduktionsmallProduktBaseCommand";
import { ProduktionsmallProduktinstruktionBaseCommand } from "~app/features/planera/commands/ProduktionsmallProduktinstruktionBaseCommand";
import { UpdateProduktionsmallCommand } from "~app/features/planera/commands/UpdateProduktionsmallCommand";
import { ProduktionsmallarTexts } from "~app/features/planera/produktionsmallar/produktionsmallar.texts";
import { ProduktionsmallarService } from "~app/features/planera/produktionsmallar/services/produktionsmallar.service";
import {
    IHandelssortiment,
    IHandelssortimentsgrupp,
    IProdukt,
    IProduktInstruktion,
    IProduktvalRow,
    ProduktvalEvent
} from "~app/features/planera/produktval/produktval.models";
import { ProduktvalComponent } from "../../../produktval/produktval.component";
import { IProduktionsmall, IProduktionsmallRefreshItem, ProduktionsmallProduktinstruktion, ProduktionsmallRefreshMode } from "../../produktionsmall-produktval.models";
import { ProduktionsmallValidation } from "./produktionsmall-accordion-body.validation";

@Component({
    selector: "rask-produktionsmall-accordion-body",
    templateUrl: "./produktionsmall-accordion-body.component.html",
    styleUrls: ["./produktionsmall-accordion-body.component.scss"],
    providers: [CommandService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        SelectorComponent,
        CheckboxComponent,
        InfoComponent,
        ProduktvalComponent,
        ButtonComponent,
        SaveButtonComponent
    ]
})
export class ProduktionsmallAccordionBodyComponent implements OnInit, OnDestroy {
    @Input() canEdit: boolean;
    ckleverantorer: SimpleSelectionItem[];
    texts = ProduktionsmallarTexts.texts;
    commonTexts = CommonTexts.texts;
    filteredHandelssortimentsgrupper: IHandelssortimentsgrupp[];
    form: UntypedFormGroup;
    handelssortimentsgruppProduktvalsrader: { [key: string]: IProduktvalRow[] };
    @Input() isCKMall: boolean;
    @Input() mall: IProduktionsmall;
    produktionsomraden: SimpleSelectionItem[];
    @Output() refreshRequest: EventEmitter<IProduktionsmallRefreshItem> = new EventEmitter<IProduktionsmallRefreshItem>();
    selectedProduktionsOmrade: number;
    validationContext: IValidationContext;

    private readonly busyService = inject(BusyService);
    private readonly commandManager: ICommandManager;
    private readonly commandService = inject(CommandService);
    private readonly confirmModalService = inject(RaskModalService);
    private readonly destroy$ = new Subject<void>();
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly produktionsmallarService = inject(ProduktionsmallarService);
    private readonly toastService = inject(ToastService);

    private applyProduktionsMal: boolean;
    private handelssortimentsgrupper: IHandelssortimentsgrupp[];
    private mallIsDirty = false;
    private valdaproduktrader: IProduktvalRow[] = [];

    constructor() {
        this.commandManager = new CommandManager();
    }

    get canBeSaved(): boolean {
        if (!this.isDirty) {
            return false;
        }

        return this.validationContext.validate();
    }

    get isDirty(): boolean {
        const isDirty = this.form.dirty;

        return isDirty || this.mallIsDirty;
    }

    get visaEndastMedProduktionsmal(): boolean {
        return !!this.selectedProduktionsOmrade && this.applyProduktionsMal;
    }

    isDataLoaded(): boolean {
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (this.produktionsomraden && this.form && this.validationContext && this.ckleverantorer) {
            return true;
        }

        return false;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.valdaproduktrader = this.produktionsmallarService.produktvalRowsFromMall(this.mall, this.commandManager);
        this.setHandelssortimentsgruppProduktvalsraderFromValdaProduktrader();

        this.produktionsmallarService.getCKLeverantorer().subscribe(x => {
            this.ckleverantorer = x.ckLeverantorer.map(l => ({
                text: l.namn,
                value: l.id
            } as SimpleSelectionItem));
        });

        this.produktionsmallarService.produktionsMallar$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(x => {
            this.validationContext = ProduktionsmallValidation.init(this.mall, this.valdaproduktrader, x, this.isCKMall);
        });

        this.produktionsmallarService.produktionsomraden$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(x => {
            this.produktionsomraden = x.map(po => ({
                text: po.namn,
                value: po.id,
                group: po.grupp
            }));
        });

        this.produktionsmallarService.handelssortimentgrupper$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(x => {
            this.handelssortimentsgrupper = x;

            if (!this.selectedProduktionsOmrade) {
                this.filteredHandelssortimentsgrupper = this.handelssortimentsgrupper;
            }
        });

        this.createForm();
    }

    onProduktvalChangeEvent(): void {
        this.mallIsDirty = true;
        this.form.markAsDirty();
    }

    onSelectionEvent($event: ProduktvalEvent): void {
        this.handleSelectionEvent($event);
    }

    poGrouper = (ssi: SimpleSelectionItem) => ssi ? ssi.group : undefined;

    produktionsOmradeChange($event: number): void {
        this.selectedProduktionsOmrade = $event;

        if (this.selectedProduktionsOmrade) {
            const work = this.produktionsmallarService
                .getHandelssortimentGrupper(this.selectedProduktionsOmrade);
            this.busyService
                .addWork(work)
                .subscribe((x: IHandelssortimentsgrupp[]) => this.filteredHandelssortimentsgrupper = x);
        } else {
            this.filteredHandelssortimentsgrupper = this.handelssortimentsgrupper;
        }
    }

    removeMall(): void {
        this.confirmModalService.confirm(new ModalSettings(this.texts.borttagningConfirmHeader, this.texts.borttagningConfirmBody))
            .subscribe((modalResponse) => {
                if (modalResponse === ConfirmDialogResponse.cancel) {
                    return;
                }

                if (this.mall.isNew) {
                    this.commandManager.clear();
                    this.toastService.add(this.texts.mallenBorttagen);
                    this.setPristine();
                    this.refreshParent(this.mall, ProduktionsmallRefreshMode.Delete);
                } else {
                    this.commandManager.updateCommand(new DeleteProduktionsmallCascadeCommand(this.mall.guid));
                    const work = from(this.commandService.pushCommands(this.commandManager));

                    this.busyService
                        .addWork(work)
                        .subscribe(() => {
                            this.commandManager.clear();
                            this.toastService.add(this.texts.mallenBorttagen);
                            this.setPristine();
                            this.refreshParent(this.mall, ProduktionsmallRefreshMode.Delete);
                        });
                }
            });
    }

    saveAll(useToast: boolean): void {
        if (!this.canBeSaved || this.validationContext.hasErrors(ValidationScopes.spara)) {
            return;
        }
        if (this.mall.isNew) {
            // If new produktionsmall
            this.commandManager.updateCommand(new AddProduktionsmallCommand(this.mall.guid, this.mall.benamning, this.mall.vardekap, this.mall.ckLeverantorId));
        } else {
            // Always trigger update on produktionsmall to get new UBy and UDate
            this.commandManager.updateCommand(new UpdateProduktionsmallCommand(this.mall.guid, this.mall.benamning, this.mall.vardekap, this.mall.ckLeverantorId));
        }

        const work = from(this.commandService.pushCommands(this.commandManager));
        this.busyService
            .addWork(work)
            .subscribe(() => {
                this.commandManager.clear();
                if (useToast) {
                    this.toastService.add(this.texts.mallenSparades);
                }

                this.produktionsmallarService.refreshHandelssortimentGrupper();
                this.setPristine();
                this.refreshParent(this.mall, ProduktionsmallRefreshMode.Save);
            });
    }

    ssItemFormatter = (ssi: SimpleSelectionItem) => ssi ? ssi.value : undefined;

    ssItemViewFormatter = (ssi: SimpleSelectionItem) => ssi ? ssi.text : undefined;

    toggleProduktionsMal(val: boolean): void {
        this.applyProduktionsMal = val;
    }

    private createForm(): void {
        this.form = this.formBuilder.group({
            benamning: this.formBuilder.control(this.mall.benamning),
            vardekap: this.formBuilder.control(this.mall.vardekap),
            ckLeverantorId: this.formBuilder.control(this.mall.ckLeverantorId)
        });

        this.setPristine();

        this.form.get("benamning").valueChanges.subscribe((newNamn: string) => this.mall.benamning = newNamn);
        this.form.get("vardekap").valueChanges.subscribe((newVardekap: boolean) => this.mall.vardekap = newVardekap);
        this.form.get("ckLeverantorId").valueChanges.subscribe((newCKLeverantorId?: number) => {
            this.mall.ckLeverantorId = newCKLeverantorId;
        });
    }

    private handelssortimentComparer(hs: IHandelssortiment, prod?: IProdukt):
        (value: IProduktvalRow, index: number, array: IProduktvalRow[]) => boolean {
        return (value: IProduktvalRow, index: number, array: IProduktvalRow[]) => hs.handelssortimentId === value.handelssortimentId &&
            (!value.produktId || (prod !== null && prod !== undefined && value.produktId === prod.produktId));
    }

    private handleCheckedSelectionEvent(
        handelssortiment: IHandelssortiment,
        produkt: IProdukt,
        pin: IProduktInstruktion,
        matchedRow: IProduktvalRow,
        rowsForHandelssortiment: IProduktvalRow[]): void {
        if (this.shouldAddToProduktrader(produkt, matchedRow, rowsForHandelssortiment)) {
            matchedRow = this.produktionsmallarService
                .produktvalRowFromHandelssortiment(this.mall, handelssortiment as ProduktionsmallHandelssortimentDTO, this.commandManager);
            this.valdaproduktrader.push(matchedRow);
        }

        if (produkt) {
            matchedRow.produktId = produkt.produktId;
            matchedRow.produkt = produkt.namn;
        }

        if (pin) {
            if (!this.isProduktinstruktionSet(matchedRow.handelssortimentId, matchedRow.produktId)) {
                matchedRow.setMedTillUtbytessimulering(pin.produktinstruktionId);
            }

            const pinne = new ProduktionsmallProduktinstruktion(pin.produktinstruktionId, pin.namn, pin.puid);
            matchedRow.produktinstruktioner.push(pinne);
        }
    }

    private handleSelectionEvent($event: ProduktvalEvent): void {
        this.mallIsDirty = true;
        this.form.markAsDirty();

        const hs = $event.row.handelssortiment;
        const prod = $event.row.produkt;
        const pin = $event.row.produktinstruktion;

        hs.handelssortimentsgruppNamn = $event.row.handelssortimentsgruppNamn;

        const rowsForHandelssortiment = this.valdaproduktrader
            .filter(x => x.handelssortimentId === hs.handelssortimentId);
        const matchedRow = this.valdaproduktrader.filter(this.handelssortimentComparer(hs, prod))[0];

        if ($event.selectionEventType === SelectionEventType.Checked) {
            this.handleCheckedSelectionEvent(hs, prod, pin, matchedRow, rowsForHandelssortiment);
        } else if ($event.selectionEventType === SelectionEventType.Unchecked) {
            this.handleUncheckedSelectionEvent(hs, prod, pin, matchedRow, rowsForHandelssortiment);
        }

        this.validationContext.validate();
        this.triggerCommandFromEvent($event);
        this.setHandelssortimentsgruppProduktvalsraderFromValdaProduktrader();
    }

    private handleUncheckedSelectionEvent(
        handelssortiment: IHandelssortiment,
        produkt: IProdukt,
        pin: IProduktInstruktion,
        matchedRow: IProduktvalRow,
        rowsForHandelssortiment: IProduktvalRow[]): void {
        if (matchedRow) {
            if (pin) {
                const pinne = matchedRow.produktinstruktioner.filter(g => g.id === pin.produktinstruktionId)[0];

                if (matchedRow.medTillUtbytessimulering === pinne.id) {
                    matchedRow.setMedTillUtbytessimulering(undefined);
                }

                const index = matchedRow.produktinstruktioner.indexOf(pinne);
                matchedRow.produktinstruktioner.splice(index, 1);
                // sätt nästa pin till utbytessimulering om man tagit bort den som tidigare var vald
                if (matchedRow.produktinstruktioner.length > 0 &&
                    !this.isProduktinstruktionSet(matchedRow.handelssortimentId, matchedRow.produktId)) {
                    matchedRow.setMedTillUtbytessimulering(matchedRow.produktinstruktioner[0].id);
                }
            } else if (produkt && rowsForHandelssortiment.length === 1) {
                // I am the last of my kind -> reset everything related to produkt but keep the row
                matchedRow.unsetProdukt();
            } else {
                const index = this.valdaproduktrader.indexOf(matchedRow);
                this.valdaproduktrader.splice(index, 1);
            }
        } else if (this.shouldRemoveFromProduktrader(produkt, this.valdaproduktrader)) {
            // Tagit bort helt handelssortiment
            remove(this.valdaproduktrader, p => p.handelssortimentId === handelssortiment.handelssortimentId);
        }
    }

    private isProduktinstruktionSet(handelssortimentId: number, produktId: number): boolean {
        return this.valdaproduktrader
            .some(x => x.medTillUtbytessimulering && x.handelssortimentId === handelssortimentId && x.produktId === produktId);
    }

    private refreshParent(mall: ProduktionsmallQueryResponseDTO, mode: ProduktionsmallRefreshMode): void {
        this.refreshRequest.emit({
            mall: mall,
            mode: mode
        } as IProduktionsmallRefreshItem);
    }

    private setHandelssortimentsgruppProduktvalsraderFromValdaProduktrader(): void {
        orderBy(this.valdaproduktrader, p => p.handelssortimentProduktAsText);
        this.handelssortimentsgruppProduktvalsrader = groupBy(this.valdaproduktrader, p => p.handelssortimentsgrupp);
    }

    private setPristine(): void {
        this.form.markAsPristine();
        this.mallIsDirty = false;
    }

    private shouldAddToProduktrader(produkt: IProdukt, matchedRow: IProduktvalRow, rowsForHandelssortiment: IProduktvalRow[]): boolean {
        const anyHs = rowsForHandelssortiment.length > 0;
        return (!matchedRow && !!produkt) || (!produkt && !anyHs);
    }

    private shouldRemoveFromProduktrader(produkt: IProdukt, rowsForHandelssortiment: IProduktvalRow[]): boolean {
        const anyHs = rowsForHandelssortiment.length > 0;
        return (anyHs && !produkt);
    }

    private triggerCommandFromEvent($event: ProduktvalEvent): void {
        if ($event.row.produkt && !$event.row.produktinstruktion) {
            let cmd: ProduktionsmallProduktBaseCommand;
            if ($event.selectionEventType === SelectionEventType.Checked) {
                cmd = new AddProduktionsmallProduktCommand(
                    this.mall.guid,
                    $event.row.handelssortiment.handelssortimentId,
                    $event.row.produkt.produktId
                );
            } else {
                cmd = new DeleteProduktionsmallProduktCommand(
                    this.mall.guid,
                    $event.row.handelssortiment.handelssortimentId,
                    $event.row.produkt.produktId
                );
            }
            this.commandManager.updateCommand(cmd);
        } else if ($event.row.produkt && $event.row.produktinstruktion) {
            let cmd: ProduktionsmallProduktinstruktionBaseCommand;
            if ($event.selectionEventType === SelectionEventType.Checked) {
                cmd = new AddProduktionsmallProduktinstruktionCommand(
                    this.mall.guid,
                    $event.row.handelssortiment.handelssortimentId,
                    $event.row.produkt.produktId,
                    $event.row.produktinstruktion.produktinstruktionId,
                    false);
            } else {
                cmd = new DeleteProduktionsmallProduktinstruktionCommand(
                    this.mall.guid,
                    $event.row.handelssortiment.handelssortimentId,
                    $event.row.produkt.produktId,
                    $event.row.produktinstruktion.produktinstruktionId);
            }
            this.commandManager.updateCommand(cmd);
        } else if (!$event.row.produkt) {
            let cmd: ProduktionsmallHandelssortimentBaseCommand;
            if ($event.selectionEventType === SelectionEventType.Checked) {
                cmd = new AddProduktionsmallHandelssortimentCommand(this.mall.guid, $event.row.handelssortiment.handelssortimentId);
            } else {
                cmd = new DeleteProduktionsmallHandelssortimentCommand(this.mall.guid, $event.row.handelssortiment.handelssortimentId);
            }
            this.commandManager.updateCommand(cmd);
        }
    }
}
